/* ============================================================================
   bsfn-agent.js — lets AI shopping agents drive the BSFN storefront through the
   same paths a shopper's own clicks take, and see what the shopper does.

   ORIGIN     ORIGINAL — authored for BSFN against the Shopify and Chrome
              platform documents cited below. No code was copied or adapted.

   REFERENCE  Standard actions   shopify.dev/docs/api/storefront-events-and-actions/actions/configure
              Standard events    shopify.dev/docs/api/storefront-events-and-actions/events/dispatch
              Storefront WebMCP  shopify.dev/docs/api/web-mcp
              WebMCP (Chrome)    developer.chrome.com/docs/ai/webmcp/imperative-api
              Tool design        developer.chrome.com/docs/ai/webmcp/best-practices
              Product JSON       shopify.dev/docs/api/ajax/reference/product

   1  ACTIONS  Shopify's agent cart tools (update_cart, cancel_cart) call
               Shopify.actions.updateCart, and proceed-to-cart calls openCart.
               Their built-in defaults only recognise other themes' cart markup,
               so on this theme they would reload the page and send the shopper
               to /cart. We configure both. updateCart lets Shopify write the
               cart, then raises the theme's own cart bus — 'bsfn:cart:updated'
               with source 'agent' — so the drawer and the cart page re-render
               through the Section Rendering API exactly as they do after a
               shopper's add. openCart opens the drawer. Configuring at all turns
               Shopify's reload fallback off, so the handler owns the refresh.

   2  EVENTS   BSFN.std dispatches Shopify's standard storefront events for the
               cart changes the theme makes itself (an action emits its own, so
               nothing here runs for an agent's change), plus page, product,
               collection and cart views, so apps and agents see what the
               shopper does without reading our markup.

   3  TOOLS    Where the browser exposes WebMCP, registers three BSFN tools the
               platform set does not cover: the printed Facts label, the
               Subscribe & Save options, and adding a product on a subscription
               plan. Each returns a JSON string and never throws at the agent.

   PUBLIC API  window.BSFN.std.cartLines(el, action, context, lines) -> {done, fail}
               window.BSFN.std.cartNote(el, context, note)           -> {done, fail}
   DATA        #bsfn-agent-context — rendered by snippets/bsfn-agent-context
   ========================================================================== */
(function () {
  'use strict';

  var BSFN = (window.BSFN = window.BSFN || {});
  if (BSFN.std) return;

  var CTX = null;

  function context() {
    if (CTX) return CTX;
    CTX = {};
    try {
      var el = document.getElementById('bsfn-agent-context');
      if (el) CTX = JSON.parse(el.textContent) || {};
    } catch (e) { CTX = {}; }
    return CTX;
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function drawer() { return document.querySelector('[data-bsfn-cart-drawer]'); }

  /* ---------- 2. standard events --------------------------------------------
     The library is the module Shopify's own action runtime imports, so the
     browser already has it cached. snippets/bsfn-agent-context loads it with
     an inline <script type="module"> and exposes it as window.StandardEvents —
     Shopify's documented pattern for a theme without modules. It is NOT
     imported from here: Shopify's asset minifier rewrites a dynamic import()
     in a classic script into require(), which does not exist in a browser.
     Every dispatch is best-effort: an event failing must never get between a
     shopper and the cart. */

  var libPromise = null;
  function lib() {
    if (!libPromise) {
      libPromise = new Promise(function (resolve) {
        if (window.StandardEvents) { resolve(window.StandardEvents); return; }
        var settled = false;
        function finish() {
          if (settled) return;
          settled = true;
          resolve(window.StandardEvents || null);
        }
        document.addEventListener('bsfn:standard-events', finish, { once: true });
        // A module that fails to load never fires the event; load always does.
        window.addEventListener('load', function () { setTimeout(finish, 0); }, { once: true });
      });
    }
    return libPromise;
  }

  function fromAjax(SE, cart) {
    if (!cart || !cart.items) return null;
    var conv = (SE.CartLinesUpdateEvent && SE.CartLinesUpdateEvent.createCartFromAjaxResponse) ||
               (SE.CartViewEvent && SE.CartViewEvent.createCartFromAjaxResponse);
    try { return conv ? conv(cart) : null; } catch (e) { return null; }
  }

  /* An operation event fires as the change starts and carries a promise that
     settles with the outcome. The library loads asynchronously, so the
     operation can finish first; the outcome is held until the event is out. */
  function operation(className, el, payload) {
    var outcome = null;
    var live = null;

    function settle() {
      if (!live || !outcome) return;
      if (outcome.ok) {
        live.deferred.resolve({ cart: fromAjax(live.SE, outcome.cart) });
      } else {
        live.deferred.reject(outcome.error || new Error('cart request failed'));
        try {
          (el || document).dispatchEvent(new live.SE.CartErrorEvent({
            error: (outcome.error && outcome.error.message) || 'The cart could not be updated.',
            code: 'SERVICE_UNAVAILABLE'
          }));
        } catch (e) {}
      }
      live = null;
    }

    lib().then(function (SE) {
      if (!SE || !SE[className]) return;
      var deferred = SE[className].createPromise();
      deferred.promise.catch(function () {}); // listeners attach their own handlers
      payload.promise = deferred.promise;
      try { (el || document).dispatchEvent(new SE[className](payload)); }
      catch (e) { return; }
      live = { SE: SE, deferred: deferred };
      settle();
    });

    return {
      done: function (cart) { if (!outcome) { outcome = { ok: true, cart: cart }; settle(); } },
      fail: function (err) { if (!outcome) { outcome = { ok: false, error: err }; settle(); } }
    };
  }

  var NOOP = { done: function () {}, fail: function () {} };

  /* action 'add' lines carry merchandiseId; 'update' / 'remove' carry the
     AJAX line key as id. A line without its identifier is dropped. */
  function cartLines(el, action, ctx, lines) {
    var key = action === 'add' ? 'merchandiseId' : 'id';
    var clean = (lines || []).filter(function (l) {
      return l && l[key] != null && String(l[key]) !== '' && String(l[key]) !== 'null';
    }).map(function (l) {
      var o = { quantity: Math.max(0, parseInt(l.quantity, 10) || 0) };
      o[key] = String(l[key]);
      return o;
    });
    if (!clean.length) return NOOP;
    return operation('CartLinesUpdateEvent', el, { action: action, context: ctx, lines: clean });
  }

  function cartNote(el, ctx, note) {
    return operation('CartNoteUpdateEvent', el, { context: ctx, note: String(note == null ? '' : note) });
  }

  function view(className, el, payload) {
    if (!payload) return;
    lib().then(function (SE) {
      if (!SE || !SE[className]) return;
      try { (el || document).dispatchEvent(new SE[className](payload)); } catch (e) {}
    });
  }

  BSFN.std = { cartLines: cartLines, cartNote: cartNote };

  function pageViews() {
    var c = context();
    var main = document.getElementById('MainContent') || document;
    view('PageViewEvent', document, {
      page: { template: c.template || 'page', title: document.title, url: window.location.href }
    });
    var v = c.views || {};
    if (v.product) view('ProductViewEvent', main, v.product);
    if (v.collection) view('CollectionViewEvent', main, v.collection);
    if (v.cart) view('CartViewEvent', main, v.cart);
  }

  // The drawer re-renders its own view payload after every mutation, so the
  // event carries the cart as the shopper sees it at the moment it opens.
  document.addEventListener('bsfn:drawer:opened', function () {
    var d = drawer();
    var data = d && d.querySelector('[data-cd-view]');
    if (!data) return;
    try { view('CartViewEvent', d, JSON.parse(data.textContent)); } catch (e) {}
  });

  /* ---------- 1. standard actions ------------------------------------------ */

  var configured = false;
  function configureActions() {
    var A = window.Shopify && window.Shopify.actions;
    if (configured || !A) return;
    configured = true;

    if (A.updateCart && typeof A.updateCart.configure === 'function') {
      A.updateCart.configure({
        eventTarget: function () { return drawer(); },
        handler: function (defaultHandler, payload) {
          return defaultHandler().then(function (result) {
            if (result && result.cart) {
              var adding = !!(payload && payload.lines && payload.lines.some(function (l) {
                return l && l.merchandiseId && !l.id && l.quantity > 0;
              }));
              var rejected = !!(result.userErrors && result.userErrors.length);
              try {
                document.dispatchEvent(new CustomEvent('bsfn:cart:updated', {
                  detail: { source: 'agent', action: adding && !rejected ? 'add' : 'update' }
                }));
              } catch (e) {}
            }
            return result;
          });
        }
      });
    }

    if (A.openCart && typeof A.openCart.configure === 'function') {
      A.openCart.configure({
        handler: function () {
          if (context().template === 'cart') {
            var main = document.getElementById('MainContent');
            if (main) { main.scrollIntoView(); main.focus({ preventScroll: true }); }
          } else if (BSFN.cart && typeof BSFN.cart.open === 'function') {
            BSFN.cart.open();
          } else {
            window.location.href = root() + 'cart';
          }
          return Promise.resolve();
        }
      });
    }
  }

  /* ---------- 3. WebMCP tools ----------------------------------------------- */

  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    return { amount: (Math.round(Number(cents) || 0) / 100).toFixed(2), currencyCode: cur };
  }

  function args(input) {
    if (typeof input === 'string') { try { return JSON.parse(input) || {}; } catch (e) { return {}; } }
    return input || {};
  }

  function reply(obj) { return JSON.stringify(obj); }
  function refuse(msg) { return reply({ ok: false, error: msg }); }

  function handleFrom(input) {
    var h = input.product_handle;
    if (h) {
      h = String(h).trim();
      var m = h.match(/\/products\/([^/?#]+)/);   // a full product URL is accepted too
      if (m) h = m[1];
      return decodeURIComponent(h);
    }
    var c = context();
    return c.product && c.product.handle;
  }

  function productUrl(handle) { return root() + 'products/' + encodeURIComponent(handle); }

  function productJson(handle) {
    return fetch(productUrl(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status === 404 ? 'not_found' : 'http_' + r.status); return r.json(); });
  }

  function plansFor(product, variant) {
    var plans = [];
    (product.selling_plan_groups || []).forEach(function (g) {
      (g.selling_plans || []).forEach(function (sp) {
        var alloc = (variant.selling_plan_allocations || []).filter(function (a) { return a.selling_plan_id === sp.id; })[0];
        if (!alloc) return;
        var pct = null;
        (sp.price_adjustments || []).forEach(function (pa) {
          if (pct === null && pa.value_type === 'percentage') pct = pa.value;
        });
        plans.push({
          selling_plan_id: String(sp.id),
          name: sp.name,
          delivery: (sp.options || []).map(function (o) { return o.value; }).join(', '),
          discount_percent: pct,
          price_per_delivery: money(alloc.per_delivery_price != null ? alloc.per_delivery_price : alloc.price),
          compare_at_price: money(alloc.compare_at_price != null ? alloc.compare_at_price : variant.price)
        });
      });
    });
    return plans;
  }

  function pickVariant(product, wanted) {
    var vs = product.variants || [];
    if (wanted) {
      var w = String(wanted).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
      return vs.filter(function (v) { return String(v.id) === w; })[0] || null;
    }
    return vs.filter(function (v) { return v.available; })[0] || vs[0] || null;
  }

  var TOOLS = [
    {
      name: 'get_nutrition_facts',
      description:
        'Get the Nutrition Facts or Supplement Facts label printed on a Blood Sugar Friendly product, ' +
        'row by row as it appears on the package (amount and % Daily Value), with the serving size, ' +
        'the ingredient statement, the allergen statement and the facility statement. ' +
        'Pass the product handle from search_catalog or get_product; on a product page it defaults to that product.',
      inputSchema: {
        type: 'object',
        properties: {
          product_handle: {
            type: 'string',
            description: 'The product handle, such as "protein-bar-reimagined-caramel-blondie", or the product URL.'
          }
        }
      },
      annotations: { readOnlyHint: true },
      execute: function (input) {
        var handle = handleFrom(args(input));
        if (!handle) return Promise.resolve(refuse('Pass product_handle: this page is not a product page.'));
        return fetch(productUrl(handle) + '?section_id=bsfn-agent-facts', { headers: { Accept: 'text/html' } })
          .then(function (r) { if (!r.ok) throw new Error(r.status === 404 ? 'not_found' : 'http_' + r.status); return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var data = doc.querySelector('[data-bsfn-agent-facts]');
            // The Section Rendering API answers 200 with an empty section when
            // the product isn't on this storefront, so no data means not found.
            if (!data) throw new Error('not_found');
            var facts = JSON.parse(data.textContent);
            facts.ok = true;
            return reply(facts);
          })
          .catch(function (e) {
            return refuse(e && e.message === 'not_found'
              ? 'No product with the handle "' + handle + '" is available on this storefront. Use search_catalog to find the handle.'
              : 'The label could not be loaded. Try again, or read the Facts panel on the product page.');
          });
      }
    },
    {
      name: 'get_subscription_options',
      description:
        'List the Subscribe & Save plans for a Blood Sugar Friendly product: each plan\'s delivery frequency, ' +
        'discount and price per delivery, next to the one-time price. Use the selling_plan_id it returns with ' +
        'add_subscription_to_cart. On a product page it defaults to that product.',
      inputSchema: {
        type: 'object',
        properties: {
          product_handle: { type: 'string', description: 'The product handle, or the product URL.' },
          variant_id: { type: 'string', description: 'Optional variant ID; defaults to the first variant in stock.' }
        }
      },
      annotations: { readOnlyHint: true },
      execute: function (input) {
        var a = args(input);
        var handle = handleFrom(a);
        if (!handle) return Promise.resolve(refuse('Pass product_handle: this page is not a product page.'));
        return productJson(handle).then(function (p) {
          var v = pickVariant(p, a.variant_id);
          if (!v) return refuse('That variant is not part of this product.');
          var plans = plansFor(p, v);
          return reply({
            ok: true,
            product: { title: p.title, handle: p.handle, url: window.location.origin + productUrl(p.handle) },
            variant: { id: String(v.id), title: v.title, available: !!v.available },
            one_time_price: money(v.price),
            subscription_required: !!p.requires_selling_plan,
            subscription_plans: plans,
            subscription_policy_url: window.location.origin + root() + 'policies/subscription-policy',
            note: plans.length ? undefined : 'This product is sold as a one-time purchase only.'
          });
        }).catch(function (e) {
          return refuse(e && e.message === 'not_found'
            ? 'No product with the handle "' + handle + '" is available on this storefront. Use search_catalog to find the handle.'
            : 'The subscription options could not be loaded. Try again.');
        });
      }
    },
    {
      name: 'add_subscription_to_cart',
      description:
        'Add a Blood Sugar Friendly product to the shopper\'s cart as a Subscribe & Save subscription: ' +
        'recurring delivery at the plan\'s discount. Takes a selling_plan_id from get_subscription_options ' +
        '(the product\'s only plan is used when there is just one). The shopper commits to recurring ' +
        'deliveries only when they complete checkout. For a one-time purchase, use update_cart.',
      inputSchema: {
        type: 'object',
        properties: {
          product_handle: { type: 'string', description: 'The product handle, or the product URL. Defaults to the product on this page.' },
          selling_plan_id: { type: 'string', description: 'The plan to subscribe on, from get_subscription_options.' },
          quantity: { type: 'integer', minimum: 1, description: 'Units per delivery. Defaults to 1.' },
          variant_id: { type: 'string', description: 'Optional variant ID; defaults to the first variant in stock.' }
        }
      },
      execute: function (input) {
        var a = args(input);
        var handle = handleFrom(a);
        var A = window.Shopify && window.Shopify.actions;
        if (!handle) return Promise.resolve(refuse('Pass product_handle: this page is not a product page.'));
        if (!A || !A.updateCart) return Promise.resolve(refuse('The cart is not available on this page. Reload and try again.'));
        var qty = Math.max(1, parseInt(a.quantity, 10) || 1);

        return productJson(handle).then(function (p) {
          var v = pickVariant(p, a.variant_id);
          if (!v) return refuse('That variant is not part of this product.');
          if (!v.available) return refuse(p.title + ' is out of stock.');
          var plans = plansFor(p, v);
          if (!plans.length) return refuse(p.title + ' has no Subscribe & Save plan. Use update_cart for a one-time purchase.');
          var plan = a.selling_plan_id
            ? plans.filter(function (x) { return x.selling_plan_id === String(a.selling_plan_id).replace(/^gid:\/\/shopify\/SellingPlan\//, ''); })[0]
            : (plans.length === 1 ? plans[0] : null);
          if (!plan) {
            return reply({
              ok: false,
              error: a.selling_plan_id ? 'That selling_plan_id is not offered on this product.' : 'This product has several plans. Pass a selling_plan_id.',
              subscription_plans: plans
            });
          }
          return A.updateCart({
            lines: [{ merchandiseId: String(v.id), quantity: qty, sellingPlanId: plan.selling_plan_id }]
          }).then(function (res) {
            if (res && res.userErrors && res.userErrors.length) {
              return reply({ ok: false, error: res.userErrors[0].message, user_errors: res.userErrors });
            }
            var cart = (res && res.cart) || {};
            return reply({
              ok: true,
              added: { product: p.title, variant_id: String(v.id), quantity: qty, plan: plan.name, price_per_delivery: plan.price_per_delivery },
              cart: { total_quantity: cart.totalQuantity, total: cart.cost && cart.cost.totalAmount },
              warnings: res && res.warnings && res.warnings.length ? res.warnings : undefined
            });
          });
        }).catch(function (e) {
          return refuse(e && e.message === 'not_found'
            ? 'No product with the handle "' + handle + '" is available on this storefront. Use search_catalog to find the handle.'
            : 'The subscription could not be added. Try again, or use the Subscribe & Save option on the product page.');
        });
      }
    }
  ];

  var registered = false;
  function registerTools() {
    if (registered) return true;
    var mc = document.modelContext || navigator.modelContext;
    if (!mc || typeof mc.registerTool !== 'function') return false;
    registered = true;
    TOOLS.forEach(function (t) {
      try { mc.registerTool(t); } catch (e) {}
    });
    return true;
  }

  /* ---------- boot ---------------------------------------------------------- */

  /* Shopify defines Shopify.actions in a module script, which runs after this
     deferred one and before DOMContentLoaded. A deferred script runs while
     readyState is already 'interactive', so readyState cannot tell us whether
     that module has run — wait for DOMContentLoaded unless the page has fully
     loaded. Registering the listener now, from a script placed above
     content_for_header, puts it ahead of any app's, so our configuration is
     the one that takes effect (only the first call does). */
  var booted = false;
  function boot() {
    if (booted) return;
    booted = true;
    configureActions();
    pageViews();
    // WebMCP is enabled by an origin-trial token that Shopify's async script
    // injects; when it lands after this point, try again once the page loads.
    if (!registerTools()) window.addEventListener('load', registerTools, { once: true });
  }

  if (document.readyState === 'complete') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
    // Belt and braces: if DOMContentLoaded had already fired, load still will.
    window.addEventListener('load', function () { boot(); configureActions(); }, { once: true });
  }
})();
